/**
 * The firm's rule for an entrance, the desk's half (#420): the hyphen is the entrance.
 *
 * `Студентска 14-Б` is entrance Б of number 14; `14Б` and `14 Б` are a house of their own.
 * The server reads and writes addresses by this rule (`AddressReading` in the shared kernel);
 * the desk needs three small pieces of it — to write a letter the dispatcher picked onto the
 * building, to tell whether what was typed already names a door, and to fold a building's
 * doors into one row for the dispatchers who want them after the address.
 */

/** A row of the address list, as much of it as folding reads. */
export interface Door {
  address: string;
  longitude: number;
  latitude: number;
  firm?: { id?: string | null; building: string; entrance?: string | null } | null;
}

/** A row of the list once a building's doors are folded: the building, and its doors. */
export type Folded<P extends Door> =
  | { kind: 'one'; place: P }
  | { kind: 'building'; place: P; doors: P[] };

/** A Latin letter typed for a Cyrillic one, as a Bulgarian phonetic keyboard has it. */
const PHONETIC: Readonly<Record<string, string>> = {
  A: 'А', B: 'Б', V: 'В', G: 'Г', D: 'Д', E: 'Е', Z: 'З', I: 'И', K: 'К', L: 'Л', M: 'М',
  N: 'Н', O: 'О', P: 'П', R: 'Р', S: 'С', T: 'Т', U: 'У', F: 'Ф', H: 'Х', C: 'Ц',
};

/**
 * An entrance as the firm stores it: one letter upper case, or a number of one or two
 * digits. Null for anything else, a key that is not one.
 */
export function canonicalEntrance(text: string): string | null {
  const trimmed = text.trim().replace(/\.$/, '');

  if (/^\d{1,2}$/.test(trimmed)) return String(Number(trimmed));

  if (!/^\p{L}$/u.test(trimmed)) return null;

  const upper = trimmed.toLocaleUpperCase('bg');

  return PHONETIC[upper] ?? upper;
}

/**
 * An address read for its entrance, as the server reads it: `вх. Б` and `вход Б` first, then a
 * letter after a hyphen on a number. Never a number after the hyphen — `14-16` is a range.
 */
export function readAddress(text: string): { building: string; entrance: string | null } {
  const tidy = (value: string) =>
    value.replace(/\s+/g, ' ').replace(/\s+,/g, ',').replace(/,(\s*,)+/g, ',').replace(/^[\s,]+|[\s,]+$/g, '');

  const said = /[,\s]*(?<![\p{L}\d])(?:вход|вх|vhod|vh)\.?\s*(\p{L}|\d{1,2})(?![\p{L}\d])/iu.exec(text);

  if (said !== null) {
    return {
      building: tidy(text.slice(0, said.index) + ' ' + text.slice(said.index + said[0].length)),
      entrance: canonicalEntrance(said[1]),
    };
  }

  const hyphen = /(?<![\p{L}\d])(\d+)\s*-\s*(\p{L})(?![\p{L}\d])/u.exec(text);

  if (hyphen !== null) {
    return {
      building: tidy(text.slice(0, hyphen.index) + hyphen[1] + text.slice(hyphen.index + hyphen[0].length)),
      entrance: canonicalEntrance(hyphen[2]),
    };
  }

  return { building: tidy(text), entrance: null };
}

/**
 * Writes a building and an entrance as one line, as the server does: on the block when there
 * is one, otherwise on the first part that ends in a house number.
 */
export function writeEntrance(building: string, entrance: string): string {
  const parts = building.split(',').map((part) => part.trim());
  const block = parts.findIndex((part) => /^(бл|bl)[\s.]/i.test(part));
  const numbered = parts.findIndex((part) => /\d+\p{L}?$/u.test(part));
  const target = block >= 0 ? block : numbered >= 0 ? numbered : 0;

  parts[target] = `${parts[target]}-${entrance}`;

  return parts.join(', ');
}

/**
 * Whether what was typed already names a door or a house letter — `14 б`, `14-б`, `14б`,
 * `14 вх б`. Then the list is shown as the server sent it, so the door typed is the first
 * row rather than folded under its building.
 */
export function namesADoor(query: string): boolean {
  return /\d(?:\s*-\s*|\s+(?:вх\.?|вход)?\s*|\s*(?:вх\.?|вход)\s*)?\p{L}(?!\p{L})/iu.test(query);
}

/** The building's own key, for grouping its doors: case and spacing do not make two buildings. */
function buildingKey(building: string): string {
  return building.toLocaleLowerCase('bg').replace(/\s+/g, ' ').trim();
}

/**
 * Folds each building's doors into one row of the list (#420, *after the address*): the
 * building, marked with how many doors it has, and the doors behind it to pick from.
 *
 * The building's row is the map's own row for it when the map sent one — removed from where
 * it was, so the building is listed once — and otherwise the first door's address without its
 * entrance, on that door's point. A house with a letter, `63а`, is never folded: it is another
 * house, not a door. Only a building with a door the firm placed is folded; a door typed and
 * not yet placed is the row it is.
 */
export function foldEntrances<P extends Door>(places: readonly P[]): Folded<P>[] {
  const doors = new Map<string, P[]>();

  for (const place of places) {
    if (place.firm?.entrance != null && place.firm.id != null) {
      const key = buildingKey(place.firm.building);
      doors.set(key, [...(doors.get(key) ?? []), place]);
    }
  }

  const rows: Folded<P>[] = [];
  const used = new Set<P>();

  for (const place of places) {
    if (used.has(place)) continue;

    const key =
      place.firm?.entrance != null && place.firm.id != null ? buildingKey(place.firm.building) : null;
    const group = key === null ? undefined : doors.get(key);

    if (key === null || group === undefined) {
      // The map's row for a building whose doors are listed is folded in with them.
      if (place.firm == null && doors.has(buildingKey(place.address))) continue;

      rows.push({ kind: 'one', place });
      used.add(place);
      continue;
    }

    const own = places.find((candidate) => candidate.firm == null && buildingKey(candidate.address) === key);
    const first = group[0];
    const building =
      own ?? ({ ...first, address: first.firm!.building, firm: null } as P);

    const sorted = [...group].sort((a, b) =>
      (a.firm!.entrance ?? '').localeCompare(b.firm!.entrance ?? '', 'bg', { numeric: true }),
    );

    rows.push({ kind: 'building', place: building, doors: sorted });
    group.forEach((door) => used.add(door));
    if (own !== undefined) used.add(own);
  }

  return rows;
}
